import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import { applyServerErrors, TextField } from '../../../components/ui/Field';
import { ErrorState } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { ApiError } from '../../../services/http';
import { teamApi, teamKeys, type Branch } from '../api';

const blank = z
  .string()
  .trim()
  .transform((v) => (v === '' ? null : v));
const schema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9_-]{1,20}$/, 'Use 1–20 letters, digits, - or _'),
  name: z.string().trim().min(1, 'Enter a name').max(150),
  phone: blank,
  email: z
    .string()
    .trim()
    .transform((v) => (v === '' ? null : v))
    .pipe(z.string().email('Enter a valid email').nullable()),
  city: blank,
  addressLine1: blank,
});
type Values = z.input<typeof schema>;
type Output = z.output<typeof schema>;

export function BranchDialog({
  open,
  onOpenChange,
  branch,
  onSaved,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  branch?: Branch;
  onSaved?: (b: Branch) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const form = useForm<Values, unknown, Output>({ resolver: zodResolver(schema) });
  useEffect(() => {
    if (open)
      form.reset({
        code: branch?.code ?? '',
        name: branch?.name ?? '',
        phone: branch?.phone ?? '',
        email: branch?.email ?? '',
        city: branch?.city ?? '',
        addressLine1: '',
      });
  }, [open, branch, form]);

  const save = useMutation({
    mutationFn: (body: Output) =>
      branch ? teamApi.updateBranch(branch.id, body) : teamApi.createBranch(body),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: teamKeys.branches });
      toast.success(branch ? 'Branch updated' : 'Branch created');
      onOpenChange(false);
      onSaved?.(saved);
    },
    onError: (err) =>
      err instanceof ApiError ? applyServerErrors(err.fieldErrors, form.setError as never) : undefined,
  });
  const e = form.formState.errors;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={branch ? `Edit ${branch.name}` : 'New branch'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="branch-form" loading={save.isPending}>
            {branch ? 'Save' : 'Create branch'}
          </Button>
        </>
      }
    >
      <form
        id="branch-form"
        noValidate
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={form.handleSubmit((v) => save.mutate(v))}
      >
        {save.error && !(save.error instanceof ApiError && save.error.code === 'VALIDATION_FAILED') ? (
          <div className="sm:col-span-2">
            <ErrorState error={save.error} />
          </div>
        ) : null}
        <TextField label="Name" required error={e.name?.message} {...form.register('name')} />
        <TextField
          label="Code"
          required
          hint="Short unique code, e.g. PUNE1"
          error={e.code?.message}
          {...form.register('code')}
        />
        <TextField label="Phone" type="tel" error={e.phone?.message} {...form.register('phone')} />
        <TextField label="Email" type="email" error={e.email?.message} {...form.register('email')} />
        <TextField label="City" {...form.register('city')} />
        <TextField label="Address" {...form.register('addressLine1')} />
      </form>
    </Dialog>
  );
}
